import React from 'react';
import { getRequestTypeLabel } from '../../../Enums/Helpers';
import { RequestTypes, VoluntaryTimeOffType } from '../../../Enums';
import i18n from '../../../../i18n';
import {
    getTimePeriodRatioList,
    getRequestTypeList,
} from '../../../Enums/Helpers/LabellingHelper';

import {
    getBatchOfDatesAsLong,
    getBatchOfDateTimesFromShifts,
    getBatchOfSegments,
    getFirstLocationFromShifts,
} from '../../../services/ShiftInfo/ShiftInfo';
import { convertToTimeOnly, getAsLongDate, getAsLongDateStartAndEnd, getAsStartAndEndTimeFormat } from '../../../../common-client2/src/services/ukgDateTime';

import moment from 'moment';

/**
 * @typedef {{
 *  requestStartDates: JSX.Element;
 *  requestStartDateSort: string;
 *  location: string;
 *  shiftTime: string;
 *  shiftTimeSort: string;
 *  segments: JSX.Element;
 *  segmentsSort: string;
 *  requestTypeLabel: string;
 * }} EntriesWithMappedProps
 * 
 * @typedef {ApiEmployee.AllEmployeeOffersReportPresentationModel & EntriesWithMappedProps} AllEmployeeOffersReportEntry
 * 
 * @typedef {{
 *   data: ApiEmployee.AllEmployeeOffersReportPresentationModel[];
 * }} AllEmployeeOffersReportProps
 * 
*/

export const tFixed = i18n.getFixedT(null, 'employmentBillboard', 'billboard');


/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
export const defaultSortType = 'asc';


export const initialStateSortValues = {
    sortColumn: 'requestStartDateSort',
    sortType: defaultSortType,
};


export const rowKey = 'id';

export const timePeriodRatioList = getTimePeriodRatioList({
    value: -1,
    label: tFixed('selectTimePeriod'),
});

export const requestTypeList = getRequestTypeList();

/**
 * @param {Shift.ShiftPresentationModel[]} shiftInfos 
 */
const generateSegmentsForReport = (shiftInfos) => (<>{shiftInfos.map((si) => getBatchOfSegments(si.segments))}</>);

/**
 * @param {Shift.ShiftPresentationModel} s 
 * @returns {string}
 */
const getShiftTimeLabel = (s) => `${convertToTimeOnly(s.startDateTime)} - ${convertToTimeOnly(s.endDateTime)}`;
/**
 * 
 * @param {ApiEmployee.AllEmployeeOffersReportPresentationModel} entry
 * @returns {string}
 */
const getVtoRequestStartDatesLabel = (entry) => {
    //Note: Do not convert to employee's timezone --show same date set by manager
    switch (entry.voluntaryTimeOffType) {
        case VoluntaryTimeOffType.SingleDay:
            return getAsLongDate(entry.requestPeriod.startDate);
        case VoluntaryTimeOffType.BlockOfDays:
            return getAsLongDateStartAndEnd(entry.requestPeriod.startDate, entry.requestPeriod.endDate);
        case VoluntaryTimeOffType.TimePeriod:
            return `${getAsLongDate(entry.requestPeriod.startDate)} ${getAsStartAndEndTimeFormat(entry.requestPeriod.startDate, entry.requestPeriod.endDate)}`;
        default:
            return '';
    }
}

/**
 * 
 * @param {ApiEmployee.AllEmployeeOffersReportPresentationModel} entry
 * @returns {string}
 */
const getRequestStartDatesLabel = (entry) => {
    if (entry.requestType === RequestTypes.voluntaryTimeOff) {
        return getVtoRequestStartDatesLabel(entry);
    }
    return getBatchOfDatesAsLong(entry.shiftInfos);
}

/**
 * 
 * @param {ApiEmployee.AllEmployeeOffersReportPresentationModel} entry
 * @returns {string}
 */
const getRequestStartDateSort = (entry) => {
    if (entry.requestType === RequestTypes.voluntaryTimeOff) {
        return getVtoRequestStartDatesLabel(entry);
    }
    return JSON.stringify(entry.shiftInfos.map((s) => s.startDateTime))
}
/**
 * @param {ApiEmployee.AllEmployeeOffersReportPresentationModel[]} reportEntries
 * @returns {AllEmployeeOffersReportEntry[]}
 */
export const mapToAllEmployeeOffersReportTableEntries = (reportEntries) => reportEntries.map(x => {
    let content = {
        ...x,
        requestTypeLabel: getRequestTypeLabel(x.requestType),
        requestStartDates: getRequestStartDatesLabel(x),
        requestStartDateSort: getRequestStartDateSort(x),
        location: getFirstLocationFromShifts(x.shiftInfos),
        shiftTime: getBatchOfDateTimesFromShifts(x.shiftInfos),
        shiftTimeSort: x.shiftInfos[0] ? getShiftTimeLabel(x.shiftInfos[0]) : "",
        segments: generateSegmentsForReport(x.shiftInfos),
        segmentsSort: JSON.stringify(x.shiftInfos.flatMap(shiftInfo => shiftInfo.segments)),
        rulesValidationComplicance: null,
    };

    const segmentSearchContent = x.shiftInfos.map(si => si.segments.map(s => `${s.costCenter}&&${s.startDateTime}&&${s.endDateTime}&&${s.primaryWorkruleRef !== null ? s.primaryWorkruleRef.qualifier : ""}&&`).join('&&')).join('&&');
    content.searchValue = JSON.stringify(
        `${content.requestName}&&${content.requestTypeLabel}&&${content.requestStartDateSort}&&${content.location}&&${segmentSearchContent}&&${x.initiator}`
    ).toLocaleLowerCase();
    return content;
});

/**
 * @param {ApiCommon.ISymbolicPeriodPresentationModel[]} data 
 */
export const mapSymbolicPeriodToRange = (data) => data.reduce((acc, x) => ({
    ...acc,
    [x.name.toString()]: [moment.utc(x.startDate), moment.utc(x.endDate)],
}), {});

/**
 * @param {ApiCommon.ISymbolicPeriodPresentationModel[]} data 
 */
export const mapPeriodsWithlabel = (data) => data.map((x) => ({
    ...x,
    label: x.name,
}));

/** @param {Date} date */
export const convertToUserExactDate = (date) => moment(date).toDate();
